import { Sport, TOOL_USER_POSITION_SITE_IDS, TradingType } from '@/constants';
import type { IToolUserPositionBookmakerPayload, IToolUserPositionPayload } from '@/types/toolUser';

/** Stored as strings so empty UI fields stay distinguishable from `0` while editing. */
export type ToolUserPositionMap = Record<number, string>;

/** DB / API row: `t` trading type, `b` bookmaker, `s` sport, `p` percent. */
interface IPositionRow {
    t: number;
    b: number;
    s: number;
    p: number;
}

export interface IToolUserPositionMaps {
    bot: ToolUserPositionMap;
    orderFootball: ToolUserPositionMap;
    orderBasketball: ToolUserPositionMap;
    botWl: ToolUserPositionMap;
    orderFootballWl: ToolUserPositionMap;
    orderBasketballWl: ToolUserPositionMap;
}

function emptyMap(defaultValue: string): ToolUserPositionMap {
    const map: ToolUserPositionMap = {};
    for (const bookmakerId of TOOL_USER_POSITION_SITE_IDS) {
        map[bookmakerId] = defaultValue;
    }
    return map;
}

function parsePositionList(json: string | null): IPositionRow[] {
    if (!json) return [];
    try {
        const parsed: unknown = JSON.parse(json);
        if (!Array.isArray(parsed)) return [];
        return parsed.filter(
            (row): row is IPositionRow =>
                !!row &&
                typeof row === 'object' &&
                typeof (row as IPositionRow).t === 'number' &&
                typeof (row as IPositionRow).b === 'number' &&
                typeof (row as IPositionRow).s === 'number' &&
                typeof (row as IPositionRow).p === 'number'
        );
    } catch {
        return [];
    }
}

function fillFromRows(rows: IPositionRow[], tradingTypeId: number, sportId: number, base: ToolUserPositionMap): ToolUserPositionMap {
    for (const row of rows) {
        if (row.t !== tradingTypeId) continue;
        if (row.s !== sportId) continue;
        if (!TOOL_USER_POSITION_SITE_IDS.includes(row.b)) continue;
        base[row.b] = String(row.p);
    }
    return base;
}

export function parseToolUserPositions(positions: string | null, positionsWl: string | null): IToolUserPositionMaps {
    const main = parsePositionList(positions);
    const wl = parsePositionList(positionsWl);
    return {
        bot: fillFromRows(main, TradingType.Bot, Sport.Football, emptyMap('')),
        orderFootball: fillFromRows(main, TradingType.BetOrder, Sport.Football, emptyMap('')),
        orderBasketball: fillFromRows(main, TradingType.BetOrder, Sport.Basketball, emptyMap('')),
        botWl: fillFromRows(wl, TradingType.Bot, Sport.Football, emptyMap('')),
        orderFootballWl: fillFromRows(wl, TradingType.BetOrder, Sport.Football, emptyMap('')),
        orderBasketballWl: fillFromRows(wl, TradingType.BetOrder, Sport.Basketball, emptyMap('')),
    };
}

export function emptyToolUserPositionMaps(): IToolUserPositionMaps {
    return {
        bot: emptyMap('50'),
        orderFootball: emptyMap('0'),
        orderBasketball: emptyMap('0'),
        botWl: emptyMap(''),
        orderFootballWl: emptyMap(''),
        orderBasketballWl: emptyMap(''),
    };
}

function serializeBookmakerMap(map: ToolUserPositionMap): IToolUserPositionBookmakerPayload {
    const out: IToolUserPositionBookmakerPayload = {};
    for (const bookmakerId of TOOL_USER_POSITION_SITE_IDS) {
        const trimmed = (map[bookmakerId] ?? '').trim();
        if (trimmed === '') {
            out[bookmakerId] = null;
            continue;
        }
        const n = Number(trimmed);
        out[bookmakerId] = Number.isFinite(n) ? n : null;
    }
    return out;
}

function emptyBookmakerPayload(): IToolUserPositionBookmakerPayload {
    const out: IToolUserPositionBookmakerPayload = {};
    for (const bookmakerId of TOOL_USER_POSITION_SITE_IDS) {
        out[bookmakerId] = null;
    }
    return out;
}

/** Bot positions exist only for football — nested `{ [Sport.Football]: { bookmakerId: perc } }`. */
export function serializeBotPositionPayload(map: ToolUserPositionMap): IToolUserPositionPayload {
    return {
        [Sport.Football]: serializeBookmakerMap(map),
    };
}

/** Order positions for football + basketball. */
export function serializeOrderPositionPayload(football: ToolUserPositionMap, basketball: ToolUserPositionMap): IToolUserPositionPayload {
    return {
        [Sport.Football]: serializeBookmakerMap(football),
        [Sport.Basketball]: serializeBookmakerMap(basketball),
    };
}

/** WL groups on Tradeball are not used, but backend still expects the key — send all bookmakers as `null`. */
export function emptyBotPositionPayload(): IToolUserPositionPayload {
    return {
        [Sport.Football]: emptyBookmakerPayload(),
    };
}

export function emptyOrderPositionPayload(): IToolUserPositionPayload {
    return {
        [Sport.Football]: emptyBookmakerPayload(),
        [Sport.Basketball]: emptyBookmakerPayload(),
    };
}
